import React, { useState, useEffect } from 'react';
import { EmptyState } from './shared';

const GRAD = 'linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%)';
const sansation = { fontFamily: "'Sansation', sans-serif" };

const AllIssues = ({ groupedPriorities }) => {
  const [issueFilter, setIssueFilter] = useState('all');
  const [issueCurrentPage, setIssueCurrentPage] = useState(1);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 640 && window.innerWidth < 1024);
  
  const PAGE_SIZE = 3;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
      setIsTablet(window.innerWidth >= 640 && window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const highIssues = groupedPriorities?.HIGH || [];
  const medIssues  = groupedPriorities?.MEDIUM || [];
  const lowIssues  = groupedPriorities?.LOW || [];
  const totalAll   = highIssues.length + medIssues.length + lowIssues.length;

  const allIssuesList =
    issueFilter === 'HIGH'   ? highIssues :
    issueFilter === 'MEDIUM' ? medIssues  :
    issueFilter === 'LOW'    ? lowIssues  :
    [...highIssues, ...medIssues, ...lowIssues];

  const totalPages    = Math.max(1, Math.ceil(allIssuesList.length / PAGE_SIZE));
  const safePage      = Math.min(Math.max(issueCurrentPage, 1), totalPages);
  const pageStart     = (safePage - 1) * PAGE_SIZE;
  const visibleIssues = allIssuesList.slice(pageStart, pageStart + PAGE_SIZE);

  useEffect(() => { setIssueCurrentPage(1); }, [issueFilter]);

  const getPriorityStyle = (item) => {
    const p = (item.priority_level || item.priority || '').toUpperCase();
    if (p === 'HIGH')   return { label: 'High Priority',   color: '#c1121f', bg: '#fef2f2' };
    if (p === 'LOW')    return { label: 'Low Priority',    color: '#004aad', bg: '#dce8fd' };
    return                     { label: 'Medium Priority', color: '#e85d04', bg: '#fff0d4' };
  };

  const getPageNumbers = () => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [];
    for (let p = 1; p <= totalPages; p++) {
      if (p === 1 || p === totalPages || (p >= safePage - 1 && p <= safePage + 1)) pages.push(p);
      else if (pages[pages.length - 1] !== '...') pages.push('...');
    }
    return pages;
  };

  const activeBtn   = { background: GRAD, color: '#ffffff', border: 'none' };
  const inactiveBtn = { border: '1px solid #e2e8f0', background: '#ffffff', color: '#374151' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, width: '100%', maxWidth: '100vw', overflow: 'hidden' }}>

      {/* Header */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: isMobile ? 12 : 16, 
        marginBottom: isMobile ? 12 : 16 
      }}>
        <h2 style={{ 
          ...sansation, 
          fontSize: isMobile ? 18 : (isTablet ? 20 : 22), 
          fontWeight: 700, 
          letterSpacing: '-0.02em', 
          color: '#111827', 
          margin: 0 
        }}>
          All Issues Identified
        </h2>
      </div>

      {/* Filter Pills */}
      <div style={{ 
        display: 'flex', 
        gap: isMobile ? 6 : 10, 
        marginBottom: isMobile ? 14 : 20, 
        flexWrap: 'wrap',
        overflowX: isMobile ? 'auto' : 'visible',
        WebkitOverflowScrolling: 'touch',
        paddingBottom: isMobile ? 2 : 0,
      }}>
        {[
          { key: 'all',    label: `All Issues (${totalAll})` },
          { key: 'HIGH',   label: `High Priority (${highIssues.length})` },
          { key: 'MEDIUM', label: `Medium Priority (${medIssues.length})`},
          { key: 'LOW',    label: `Low Priority (${lowIssues.length})` },
        ].map(f => (
          <button key={f.key}
            onClick={() => { setIssueFilter(f.key); setIssueCurrentPage(1); }}
            style={{
              padding: isMobile ? '5px 12px' : '6px 16px', 
              borderRadius: 999,
              fontSize: isMobile ? 10 : 11, 
              fontWeight: 600,
              cursor: 'pointer', 
              transition: 'all 0.12s',
              flexShrink: 0,
              whiteSpace: 'nowrap',
              ...(issueFilter === f.key ? activeBtn : inactiveBtn),
            }}
          >{f.label}</button>
        ))}
      </div>

      {/* Table / Card Container */}
      <div style={{
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: totalPages > 1 ? (isMobile ? '10px 10px 0 0' : '12px 12px 0 0') : (isMobile ? '10px' : '12px'),
        overflow: 'hidden',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}>
        {visibleIssues.length > 0 ? (
          isMobile ? (
            /* Mobile Card View */
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {visibleIssues.map((issue, i) => {
                const st    = getPriorityStyle(issue);
                const title = issue.priority_action_name || issue.ISSUE_TITLE || issue.title || '';
                const desc  = issue.priority_action_details || issue.ISSUE_DESC || issue.description || '';
                const slide = issue.slide_ref || issue.ISSUE_SLIDES || '';
                return (
                  <div key={pageStart + i}
                    style={{
                      padding: '14px 12px',
                      borderBottom: i < visibleIssues.length - 1 ? '1px solid #f1f5f9' : 'none',
                    }}
                  >
                    {/* Priority & Slide */}
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between', 
                      marginBottom: 8,
                      gap: 8,
                    }}>
                      <span style={{
                        display: 'inline-flex', 
                        alignItems: 'center',
                        padding: '3px 8px', 
                        borderRadius: 100,
                        fontSize: 9, 
                        fontWeight: 600,
                        background: st.bg, 
                        color: st.color,
                        flexShrink: 0,
                      }}>
                        {st.label}
                      </span>
                      {slide && (
                        <span style={{ 
                          fontSize: 10, 
                          fontWeight: 600, 
                          color: '#64748b',
                          flexShrink: 0,
                        }}>
                          {slide}
                        </span>
                      )}
                    </div>

                    {/* Title */}
                    <p style={{ 
                      ...sansation, 
                      fontSize: 13, 
                      fontWeight: 600, 
                      color: '#1e293b', 
                      lineHeight: 1.5, 
                      margin: '0 0 6px',
                      wordBreak: 'break-word',
                    }}>
                      {title}
                    </p>

                    {/* Description */}
                    <p style={{ 
                      fontSize: 12, 
                      color: '#475569', 
                      lineHeight: 1.5, 
                      margin: 0,
                      wordBreak: 'break-word',
                    }}>
                      {desc}
                    </p>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Desktop Table View */
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '2px solid #dce8fd' }}>
                  {[
                    { label: 'Priority',    width: 130   },
                    { label: 'Issue',       width: 200   },
                    { label: 'Description', width: 'auto'},
                    { label: 'Slide(s)',    width: 80    },
                  ].map(({ label, width }) => (
                    <th key={label} style={{
                      padding: '10px 16px', 
                      textAlign: 'left',
                      fontSize: 10, 
                      fontWeight: 600,
                      letterSpacing: '0.08em', 
                      textTransform: 'uppercase',
                      color: '#64748b', 
                      width,
                    }}>
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleIssues.map((issue, i) => {
                  const st    = getPriorityStyle(issue);
                  const title = issue.priority_action_name || issue.ISSUE_TITLE || issue.title || '';
                  const desc  = issue.priority_action_details || issue.ISSUE_DESC || issue.description || '';
                  const slide = issue.slide_ref || issue.ISSUE_SLIDES || '';
                  return (
                    <tr key={pageStart + i}
                      style={{ 
                        borderBottom: i < visibleIssues.length - 1 ? '1px solid #f1f5f9' : 'none', 
                        verticalAlign: 'top', 
                        transition: 'background 0.1s' 
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{
                          display: 'inline-flex', 
                          alignItems: 'center',
                          padding: '3px 10px', 
                          borderRadius: 100,
                          fontSize: 10, 
                          fontWeight: 600,
                          background: st.bg, 
                          color: st.color,
                        }}>
                          {st.label}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <p style={{ 
                          ...sansation, 
                          fontSize: 13, 
                          fontWeight: 600, 
                          color: '#1e293b', 
                          lineHeight: 1.5, 
                          margin: 0 
                        }}>
                          {title}
                        </p>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <p style={{ 
                          fontSize: 13, 
                          color: '#475569', 
                          lineHeight: 1.5, 
                          margin: 0 
                        }}>
                          {desc}
                        </p>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        {slide
                          ? <span style={{ fontSize: 11, fontWeight: 600, color: '#64748b' }}>{slide}</span>
                          : <span style={{ color: '#cbd5e1', fontSize: 13 }}>—</span>
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )
        ) : (
          <div style={{ padding: isMobile ? '32px 16px' : '48px 28px' }}>
            <EmptyState icon="CheckCircle2" title="No issues found" desc="Issue data will populate from your report." />
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', 
          gap: isMobile ? 4 : 6,
          padding: isMobile ? '10px 12px' : '14px 28px',
          background: '#ffffff',
          border: '1px solid #e2e8f0', 
          borderTop: 'none',
          borderRadius: isMobile ? '0 0 10px 10px' : '0 0 12px 12px',
          flexWrap: 'wrap',
        }}>
          <button
            onClick={() => setIssueCurrentPage(p => Math.max(1, p - 1))}
            disabled={safePage === 1}
            style={{ 
              width: isMobile ? 30 : 34, 
              height: isMobile ? 30 : 34, 
              borderRadius: isMobile ? 6 : 8, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              cursor: safePage === 1 ? 'not-allowed' : 'pointer', 
              opacity: safePage === 1 ? 0.3 : 1, 
              fontSize: isMobile ? 14 : 16,
              flexShrink: 0,
              ...inactiveBtn 
            }}>
            ‹
          </button>

          {getPageNumbers().map((p, idx) =>
            p === '...' ? (
              <span key={`e-${idx}`} style={{ 
                width: isMobile ? 30 : 34, 
                textAlign: 'center', 
                fontSize: isMobile ? 12 : 13, 
                color: '#94a3b8',
                flexShrink: 0,
              }}>
                …
              </span>
            ) : (
              <button key={p} onClick={() => setIssueCurrentPage(p)}
                style={{ 
                  width: isMobile ? 30 : 34, 
                  height: isMobile ? 30 : 34, 
                  borderRadius: isMobile ? 6 : 8, 
                  fontSize: isMobile ? 12 : 13, 
                  fontWeight: p === safePage ? 700 : 500, 
                  cursor: 'pointer', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  flexShrink: 0,
                  ...(p === safePage ? activeBtn : inactiveBtn) 
                }}>
                {p}
              </button>
            )
          )}

          {/* <span style={{ 
            padding: isMobile ? '0 8px' : '0 14px', 
            height: isMobile ? 30 : 34, 
            display: 'flex', 
            alignItems: 'center', 
            fontSize: isMobile ? 10 : 12, 
            fontWeight: 500, 
            color: '#94a3b8', 
            borderRadius: isMobile ? 6 : 8,
            flexShrink: 0,
            whiteSpace: 'nowrap',
            ...inactiveBtn 
          }}>
            {allIssuesList.length} issues
          </span> */}

          <button
            onClick={() => setIssueCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={safePage === totalPages}
            style={{ 
              width: isMobile ? 30 : 34, 
              height: isMobile ? 30 : 34, 
              borderRadius: isMobile ? 6 : 8, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              cursor: safePage === totalPages ? 'not-allowed' : 'pointer', 
              opacity: safePage === totalPages ? 0.3 : 1, 
              fontSize: isMobile ? 14 : 16,
              flexShrink: 0,
              ...inactiveBtn 
            }}>
            ›
          </button>
        </div>
      )}

    </div>
  );
};

export default AllIssues;